// src/devices/forms/DeviceTankAssignmentFormContent.tsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Stack,
  Divider,
  CircularProgress,
  Alert,
} from '@mui/material';
import { getAllContainers, assignTankToDevice } from '../../containers/services';
import { IContainer } from '../../model';

// Use a type alias instead of an empty interface
type TankConfigOption = IContainer;

interface DeviceTankAssignmentFormContentProps {
  deviceSerial: string;
  onSaved?: () => void;
  onChangeError?: (msg: string | null) => void;
  onChangeSuccessMessage?: (msg: string | null) => void;
}

const DeviceTankAssignmentFormContent: React.FC<DeviceTankAssignmentFormContentProps> = ({
  deviceSerial,
  onSaved,
  onChangeError,
  onChangeSuccessMessage,
}) => {
  const [tankConfigs, setTankConfigs] = useState<TankConfigOption[]>([]);
  const [selectedConfig, setSelectedConfig] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchTankConfigs = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch available tanks from API
        const containers = await getAllContainers(false); // false = active tanks only
        // Filter containers by status === true
        const activeContainers = containers.filter((container) => container.status === true);
        setTankConfigs(activeContainers);
        // Reset any error
        if (onChangeError) {
          onChangeError(null);
        }
      } catch (err) {
        console.error('Error fetching tank configurations:', err);
        const errorMessage = 'Failed to load tank configurations. Please try again.';
        setError(errorMessage);
        if (onChangeError) {
          onChangeError(errorMessage);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchTankConfigs();
  }, [onChangeError]);

  const currentConfig = tankConfigs.find((tc) => tc.tank_name === selectedConfig);

  // Called by parent "onSave"
  const handleSubmit = async () => {
    if (!currentConfig || submitting) return;
    try {
      setSubmitting(true);
      setError(null);
      if (onChangeError) onChangeError(null);
      // Get the tank config ID from the selected config
      const tankConfigId = currentConfig.tank_config_id;
      // Call API to assign tank to device
      const success = await assignTankToDevice(deviceSerial, tankConfigId);
      if (success) {
        onChangeSuccessMessage?.(`Tank ${currentConfig.tank_name} assigned successfully to device ${deviceSerial}`);
        onSaved?.();
      } else {
        const errorMsg = 'Failed to assign tank to device.';
        setError(errorMsg);
        onChangeError?.(errorMsg);
      }
    } catch (err) {
      console.error('Error assigning tank to device:', err);
      const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred';
      setError(errorMessage);
      onChangeError?.(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ p: 1 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>
        Edit Tank Config for Device {deviceSerial}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 3, mb: 2 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <CircularProgress />
          </Box>
        ) : (
          <FormControl fullWidth>
            <InputLabel>Select Tank Config</InputLabel>
            <Select value={selectedConfig} onChange={(e) => setSelectedConfig(e.target.value as string)}>
              {tankConfigs.length > 0 ? (
                tankConfigs.map((cfg) => (
                  <MenuItem key={cfg.tank_config_id} value={cfg.tank_name}>
                    {cfg.tank_name}
                  </MenuItem>
                ))
              ) : (
                <MenuItem disabled value="">
                  No tank configurations available
                </MenuItem>
              )}
            </Select>
          </FormControl>
        )}

        {currentConfig && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Selected Tank Details
            </Typography>
            <Stack spacing={0.5}>
              {/* <Typography variant="body2">
                <strong>Tank Type:</strong> {currentConfig.edgelink_tank_type_id}
              </Typography> */}
              <Typography variant="body2">
                <strong>UOM:</strong> {currentConfig.distance_uom.abbreviation}
              </Typography>
            </Stack>
          </Box>
        )}
      </Paper>

      <Divider sx={{ my: 2 }} />

      {/* The parent's "onSave" calls handleSubmit */}
      <Button variant="contained" onClick={handleSubmit} disabled={!selectedConfig || loading || submitting}>
        {loading ? 'Loading...' : 'Save'}
      </Button>
    </Box>
  );
};

export default DeviceTankAssignmentFormContent;
